import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Select from 'react-select'; // Add this import
import { PlusIcon, XMarkIcon, CurrencyDollarIcon, CalculatorIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format, addDays } from 'date-fns';

const CreateSale = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [shopOwners, setShopOwners] = useState([]);
  const [consignmentItems, setConsignmentItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedConsignment, setSelectedConsignment] = useState(null);
  const [salesAgentCommission, setSalesAgentCommission] = useState(null);
  const [commissionDetails, setCommissionDetails] = useState({
    amount: 0,
    basis: 'cases_sold',
    calculation: 'percentage',
    value: 0,
    displayText: ''
  });
  
  const [formData, setFormData] = useState({
    shop_owner_id: '',
    consignment_id: '',
    items: [],
    payment_type: 'cash',
    amount_paid: 0,
    due_date: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    discount: 0,
    notes: ''
  });

  // Add state for selected shop owner (for react-select)
  const [selectedShopOwner, setSelectedShopOwner] = useState(null);

  useEffect(() => {
    fetchShopOwners();
    fetchProducts();
    fetchSalesAgentCommission();
    if (user?.user_type === 'sales_agent') {
      fetchAvailableConsignmentItems();
    }
  }, []);

  useEffect(() => {
    if (formData.consignment_id) {
      const selectedItem = consignmentItems.find(item => item.consignment_item.id === formData.consignment_id);
      console.log("Selected consignment item = ", selectedItem);
      setSelectedConsignment(selectedItem);
      
      if (selectedItem) {
        const existingItemIndex = formData.items.findIndex(
          item => item.product_id === selectedItem.product.id && 
                 item.batch_id === selectedItem.batch.id
        );
        
        if (existingItemIndex >= 0) {
          toast.error('This product from this batch is already in the list');
        } else {
          const newItem = {
            product_id: selectedItem.product.id,
            product_name: selectedItem.product.name,
            batch_id: selectedItem.batch.id,
            quantity_cases: 1,
            quantity_bottles: 0,
            unit_price: selectedItem.batch.price_per_case,
            max_cases: selectedItem.consignment_item.remaining_cases,
            max_bottles: selectedItem.consignment_item.remaining_bottles,
            consignment_item_id: selectedItem.consignment_item.id,
            batch_info: selectedItem.batch,
            product_info: selectedItem.product
          };
          
          setFormData(prev => ({
            ...prev,
            items: [...prev.items, newItem]
          }));
          
          toast.success(`Added ${selectedItem.product.name} to items`);
        }
        
        setFormData(prev => ({ ...prev, consignment_id: '' }));
      }
    }
  }, [formData.consignment_id]);

  useEffect(() => {
    if (formData.payment_type === 'cash' && formData.amount_paid === 0) {
      setFormData(prev => ({
        ...prev,
        amount_paid: calculateTotal()
      }));
    }
  }, [formData.payment_type, formData.items, formData.discount]);

  useEffect(() => {
    calculateCommission();
  }, [formData.items, formData.discount]);

  const fetchShopOwners = async () => {
    try {
      const response = await api.get('users/shop-owners/', {
        params: { user_type: user?.user_type, is_active: true }        
      });
      console.log("response for Shop Owners in CreateSale.js = ", response.data);
      setShopOwners(response.data.items || []);
    } catch (error) {
      toast.error('Failed to fetch shop owners');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products/');
      setProducts(response.data.items || []);
    } catch (error) {
      toast.error('Failed to fetch products');
    }
  };

  const fetchSalesAgentCommission = async () => {
    try {
      const response = await api.get('/users/profile/me');
      if (response.data.profile) {
        setSalesAgentCommission(response.data.profile);
        setCommissionDetails({
          amount: 0,
          basis: response.data.profile.commission_basis || 'cases_sold',
          calculation: response.data.profile.commission_calculation || 'percentage',
          value: response.data.profile.commission_value || 0,
          displayText: formatCommissionText(response.data.profile)
        });
      }
    } catch (error) {
      console.error('Failed to fetch commission info:', error);
    }
  };

  const formatCommissionText = (commission) => {
    if (!commission || !commission.commission_value) return 'No commission set';
    
    const basis = commission.commission_basis === 'cases_sold' ? 'per case sold' : 'of total amount';
    const calculation = commission.commission_calculation === 'percentage' ? `${commission.commission_value}%` : `ZMW ${commission.commission_value}`;
    
    return `${calculation} ${basis}`;
  };

  const fetchAvailableConsignmentItems = async () => {
    try {
      const response = await api.get('/consignments/create-sale');
      console.log("Available consignment items = ", response.data);
      setConsignmentItems(response.data.items || []);
    } catch (error) {
      toast.error('Failed to fetch consignment items');
    }
  };

  const calculateCommission = () => {
    if (!salesAgentCommission || !salesAgentCommission.commission_value) {
      setCommissionDetails(prev => ({ ...prev, amount: 0 }));
      return;
    }

    const commissionValue = parseFloat(salesAgentCommission.commission_value) || 0;
    let commissionAmount = 0;
    const totalCases = formData.items.reduce((sum, item) => sum + (item.quantity_cases || 0), 0);
    const totalAmount = calculateTotal();

    if (salesAgentCommission.commission_basis === 'cases_sold') {
      if (salesAgentCommission.commission_calculation === 'percentage') {
        commissionAmount = totalAmount * (commissionValue / 100);
      } else {
        commissionAmount = totalCases * commissionValue;
      }
    } else {
      if (salesAgentCommission.commission_calculation === 'percentage') {
        commissionAmount = totalAmount * (commissionValue / 100);
      } else {
        commissionAmount = commissionValue;
      }
    }

    setCommissionDetails(prev => ({ 
      ...prev, 
      amount: commissionAmount,
      displayText: formatCommissionText(salesAgentCommission)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    if (formData.payment_type === 'credit' && !formData.due_date) {
      toast.error('Due date is required for credit sales');
      return;
    }

    if (formData.payment_type === 'cash' && formData.amount_paid > calculateTotal()) {
      toast.error('Payment amount cannot exceed total');
      return;
    }

    for (const item of formData.items) {
      if (item.quantity_cases > item.max_cases) {
        toast.error(`Only ${item.max_cases} cases available for ${item.product_name}`);
        return;
      }
      if (item.quantity_bottles > item.max_bottles) {
        toast.error(`Only ${item.max_bottles} bottles available for ${item.product_name}`);
        return;
      }
    }

    setLoading(true);
    try {
      console.log("sales data am sending = ", formData);
      
      const submissionData = {
        shop_owner_id: formData.shop_owner_id,
        items: formData.items.map(item => ({
          product_id: item.product_id,
          batch_id: item.batch_id,
          quantity_cases: item.quantity_cases,
          quantity_bottles: item.quantity_bottles,
          unit_price: item.unit_price,
          consignment_item_id: item.consignment_item_id
        })),
        payment_type: formData.payment_type,
        amount_paid: formData.amount_paid,
        due_date: formData.payment_type === 'credit' ? formData.due_date : null,
        discount: formData.discount,
        notes: formData.notes
      };
      
      await api.post('/sales/transactions', submissionData);
      toast.success('Sale created successfully');
      navigate('/sales');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create sale');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    toast('Select a consignment item to add to the sale');
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    const item = newItems[index];
    
    if (field === 'quantity_cases' && value > item.max_cases) {
      toast.error(`Only ${item.max_cases} cases available`);
      value = item.max_cases;
    }
    if (field === 'quantity_bottles' && value > item.max_bottles) {
      toast.error(`Only ${item.max_bottles} bottles available`);
      value = item.max_bottles;
    }
    
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => {
      return sum + (item.quantity_cases * item.unit_price) + (item.quantity_bottles * (item.unit_price / (item.product_info?.bottles_per_case || 12)));
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    return subtotal - (formData.discount || 0);
  };

  const calculateBalance = () => {
    const total = calculateTotal();
    const paid = formData.amount_paid || 0;
    return total - paid;
  };

  // Prepare options for react-select
  const shopOwnerOptions = shopOwners.map(owner => ({
    value: owner.shop_owner_id,
    label: `${owner.business_name || owner.full_name} - ${owner.town || owner.phone_number}`
  }));

  // Handle shop owner selection
  const handleShopOwnerChange = (selectedOption) => {
    setSelectedShopOwner(selectedOption);
    setFormData({...formData, shop_owner_id: selectedOption?.value || ''});
  };

  // Group consignment items by product
  const groupedConsignmentItems = consignmentItems.reduce((groups, item) => {
    const productName = item.product.name;
    if (!groups[productName]) {
      groups[productName] = [];
    }
    groups[productName].push(item);
    return groups;
  }, {});

  return (
    <div className="max-w-4xl mx-auto">
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Create New Sale
          </h2>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer Information with Searchable Select */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Customer Information</h3>
              <p className="mt-1 text-sm text-gray-500">
                Select the customer.
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Shop Owner *
                  </label>
                  <Select
                    className="mt-1"
                    classNamePrefix="select"
                    options={shopOwnerOptions}
                    value={selectedShopOwner}
                    onChange={handleShopOwnerChange}
                    placeholder="Search and select shop owner..."
                    isClearable
                    isSearchable
                    noOptionsMessage={() => "No shop owners found"}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: '#d1d5db',
                        borderRadius: '0.375rem',
                        padding: '0.125rem',
                        '&:hover': {
                          borderColor: '#d1d5db'
                        },
                        boxShadow: 'none'
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 50
                      })
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sales Agent Commission Info - Preview Banner */}
        {salesAgentCommission && salesAgentCommission.commission_value > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="bg-blue-100 rounded-full p-2 mr-3">
                  <CalculatorIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Your Commission Rate</p>
                  <p className="text-sm text-blue-700">{commissionDetails.displayText}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Estimated Commission</p>
                <p className="text-xl font-bold text-blue-600">ZMW {commissionDetails.amount.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Add Items from Consignment */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Add Items</h3>
              <p className="mt-1 text-sm text-gray-500">
                Select items from your consignment to add to the sale.
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="col-span-9 sm:col-span-9">
                <label className="block text-sm font-medium text-gray-700">
                  Available Consignment Items
                </label>
                <select
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={formData.consignment_id}
                  onChange={(e) => setFormData({...formData, consignment_id: e.target.value})}
                >
                  <option value="">Select an item to add</option>
                  {Object.entries(groupedConsignmentItems).map(([productName, items]) => (
                    <optgroup key={productName} label={productName}>
                      {items.map(item => {
                        const remainingValue = item.consignment_item.remaining_cases * item.batch.price_per_case;
                        return (
                          <option 
                            key={item.consignment_item.id} 
                            value={item.consignment_item.id}
                          >
                            {item.consignment.consignment_number} - {item.consignment.destination_town} - 
                            {item.product.unit_volume_ml}ml - 
                            Available: {item.consignment_item.remaining_cases} cases 
                            ({item.consignment_item.remaining_bottles} bottles) - 
                            ZMW {item.batch.price_per_case}/case - 
                            Total Value: ZMW {remainingValue.toFixed(2)}
                          </option>
                        );
                      })}
                    </optgroup>
                  ))}
                </select>
                <p className="mt-2 text-xs text-gray-500">
                  Select an item to add it to the sale. You can add multiple items.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Items List */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Items in Sale</h3>
              <p className="mt-1 text-sm text-gray-500">
                Review and adjust quantities.
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              {formData.items.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No items added yet. Select from the consignment items above.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.items.map((item, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg relative">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                      
                      <div className="mb-2">
                        <span className="font-medium text-gray-900">{item.product_name}</span>
                        <span className="ml-2 text-xs text-gray-500">
                          (Max: {item.max_cases} cases, {item.max_bottles} bottles)
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-4">
                          <label className="block text-xs font-medium text-gray-500">Cases</label>
                          <input
                            type="number"
                            min="0"
                            max={item.max_cases}
                            required
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            value={item.quantity_cases}
                            onChange={(e) => updateItem(index, 'quantity_cases', parseInt(e.target.value) || 0)}
                          />
                        </div>

                        <div className="col-span-4">
                          <label className="block text-xs font-medium text-gray-500">Bottles</label>
                          <input
                            type="number"
                            min="0"
                            max={item.max_bottles}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            value={item.quantity_bottles}
                            onChange={(e) => updateItem(index, 'quantity_bottles', parseInt(e.target.value) || 0)}
                          />
                        </div>

                        <div className="col-span-4">
                          <label className="block text-xs font-medium text-gray-500">Unit Price</label>
                          <div className="mt-1 p-2 bg-gray-100 rounded text-sm">
                            ZMW {item.unit_price.toFixed(2)}/case
                          </div>
                        </div>

                        <div className="col-span-6 mt-2">
                          <label className="block text-xs font-medium text-gray-500">Total</label>
                          <div className="mt-1 p-2 bg-primary-50 rounded text-sm font-medium text-primary-700">
                            ZMW {((item.quantity_cases * item.unit_price) + 
                                  (item.quantity_bottles * (item.unit_price / (item.product_info?.bottles_per_case || 12)))).toFixed(2)}
                          </div>
                        </div>
                        
                        <div className="col-span-6 mt-2">
                          <label className="block text-xs font-medium text-gray-500">Batch Info</label>
                          <div className="mt-1 text-xs text-gray-600">
                            Batch: {item.batch_info?.batch_number} | 
                            Expiry: {item.batch_info?.expiry_date ? format(new Date(item.batch_info.expiry_date), 'dd MMM yyyy') : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Information */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Payment Information</h3>
              <p className="mt-1 text-sm text-gray-500">
                Select payment type and enter amount received.
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Payment Type *
                  </label>
                  <select
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={formData.payment_type}
                    onChange={(e) => setFormData({...formData, payment_type: e.target.value, amount_paid: e.target.value === 'cash' ? calculateTotal() : 0})}
                  >
                    <option value="cash">Cash</option>
                    <option value="credit">Credit</option>
                  </select>
                </div>

                {formData.payment_type === 'cash' && (
                  <div className="col-span-6 sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Amount Paid *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={calculateTotal()}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.amount_paid}
                      onChange={(e) => setFormData({...formData, amount_paid: parseFloat(e.target.value) || 0})}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Total: ZMW {calculateTotal().toFixed(2)}
                    </p>
                  </div>
                )}

                {formData.payment_type === 'credit' && (
                  <div className="col-span-6 sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Due Date *
                    </label>
                    <input
                      type="date"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.due_date}
                      onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                      min={format(new Date(), 'yyyy-MM-dd')}
                    />
                  </div>
                )}

                <div className="col-span-6 sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Discount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={calculateSubtotal()}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={formData.discount}
                    onChange={(e) => setFormData({...formData, discount: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Totals Summary with Commission */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Summary</h3>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">ZMW {calculateSubtotal().toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Discount:</span>
                  <span className="font-medium text-red-600">-ZMW {formData.discount.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                  <span>Total:</span>
                  <span className="text-primary-600">ZMW {calculateTotal().toFixed(2)}</span>
                </div>

                {salesAgentCommission && salesAgentCommission.commission_value > 0 && (
                  <>
                    <div className="border-t border-gray-200 my-2"></div>
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <CalculatorIcon className="h-5 w-5 text-blue-500 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-gray-700">Your Commission</p>
                            <p className="text-xs text-blue-600">{commissionDetails.displayText}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Estimated Earnings</p>
                          <p className="text-xl font-bold text-blue-600">ZMW {commissionDetails.amount.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {formData.payment_type === 'cash' && (
                  <>
                    <div className="flex justify-between text-sm pt-2">
                      <span className="text-gray-600">Amount Paid:</span>
                      <span className="font-medium text-green-600">ZMW {formData.amount_paid.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold">
                      <span className="text-gray-600">Balance:</span>
                      <span className={`font-bold ${calculateBalance() > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ZMW {calculateBalance().toFixed(2)}
                      </span>
                    </div>
                    {calculateBalance() > 0 && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                        <p className="text-xs text-yellow-800">
                          <span className="font-bold">Note:</span> Customer has an outstanding balance of ZMW {calculateBalance().toFixed(2)} on this sale.
                          {formData.amount_paid > 0 && formData.amount_paid < calculateTotal() && (
                            <span> Partial payment of ZMW {formData.amount_paid.toFixed(2)} received.</span>
                          )}
                        </p>
                      </div>
                    )}
                  </>
                )}

                {formData.payment_type === 'credit' && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-xs text-blue-800">
                      <span className="font-bold">Credit Sale:</span> Full amount of ZMW {calculateTotal().toFixed(2)} will be due on {format(new Date(formData.due_date), 'dd MMM yyyy')}.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Notes</h3>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <textarea
                rows="3"
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional notes..."
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/sales')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || formData.items.length === 0}
            className="btn-primary"
          >
            {loading ? 'Creating...' : 'Create Sale'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateSale;